import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
// member_details.amountToRelease stores PAISE.
import { formatINR } from "../../utils/money";
import ServerTablePagination from "../../components/shared/ServerTablePagination";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { getCustomerOptions } from "../../utils/utils";
import { useAuth } from "../../components/shared/AuthContext";
import { BsDownload } from "react-icons/bs";
import * as XLSX from "xlsx";
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import EditBusinessDetailsModal from "../../components/shared/EditBusinessDetailsModal";
import { PageLayout, Button, Modal, Table, SegmentedControl, Select, SearchInput, Card, LogoLoader } from "../../components/ui";
import Swal from "sweetalert2";

const BusinessLoan = () => {
  const { permissions, roleName, data, jwt } = useAuth();
  const navigate = useNavigate();

  // State for data and pagination
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0
  });

  const [customerId, setCustomerId] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [error, setError] = useState("");
  const [currentFilter, setCurrentFilter] = useState("All");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showRequestedLoanModal, setShowRequestedLoanModal] = useState(false);
  const [requestedLoanData, setRequestedLoanData] = useState([]);
  const [showEditBusinessModal, setShowEditBusinessModal] = useState(false);
  const [selectedEditItem, setSelectedEditItem] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [verificationFilter, setVerificationFilter] = useState("All");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const isDataEntryOperator = roleName === "Data Entry Operator";
  const isAdmin = roleName === "admin";
  const isSuperAdmin = roleName === "superadmin";

  // Only superadmin can view all branches
  const canViewAllBranches = isSuperAdmin;

  const hasAddPermission = isDataEntryOperator || permissions.find(
    (permission) => permission.page === "businessLoan" && permission.add
  );
  const hasEditPermission = isDataEntryOperator || isAdmin || permissions.find(
    (permission) => permission.page === "businessLoan" && permission.edit
  );
  const hasViewPermission = isDataEntryOperator || isAdmin || permissions.find(
    (permission) => permission.page === "businessLoan" && permission.view
  );
  const hasDelPermission = isDataEntryOperator || permissions.find(
    (permission) => permission.page === "businessLoan" && permission.del
  );

  // Fetch branches data
  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/branch_details`);
      setBranches(response.data.data);

      if (!canViewAllBranches && data?.branchId) {
        let userBranch = response.data.data.find(b => b.id === data.branchId);
        if (!userBranch) {
          userBranch = response.data.data.find(b => b.id === Number(data.branchId));
        }
        if (!userBranch) {
          userBranch = response.data.data.find(b => Number(b.id) === data.branchId);
        }

        if (userBranch) {
          setSelectedBranch(userBranch);
        }
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  // Helper to get branch ID for filtering
  const getBranchIdFilter = () => {
    if (!canViewAllBranches && data?.branchId) return data.branchId;
    if (selectedBranch) return selectedBranch.id;
    return null;
  };

  // Unified data fetcher based on current filter
  const fetchData = useCallback(async (page = 1, limit = 15, filterType = currentFilter, search = debouncedSearch, verification = verificationFilter) => {
    setLoading(true);
    try {
      const branchId = getBranchIdFilter();
      let url = `${API_BASE_URL}/api/v2/getModelData`; // Use optimized v2 endpoint
      let params = {
        limit,
        offset: (page - 1) * limit,
        branchId: branchId || undefined,
        includeDepth: 1,
        relationshipType: 'both', // Include BelongsTo associations (like personal_details)
        searchTerm: search || undefined,
        customerVerification: verification !== "All" ? (verification === "Verified" ? "verified" : "not_verified") : undefined,
        // loanStatements is HasMany — 24 EMI rows per loan on jothitest, averaging
        // 13.8 on live. getModelDataOptimized sets subQuery:false, so the join is
        // FLATTENED and `limit` then counts EMI rows instead of applications:
        // 15 rows was only 2 customers, and jams (a disbursed loan, so 24 rows)
        // never survived the cut. `count` is computed separately and stayed
        // correct, so the page said 5 and drew 2 with no error anywhere.
        //
        // Safe to drop: this list renders name, customer ID and loan amount only.
        // Those come from personal_details (BelongsTo), proposed_loan_details
        // (HasOne) and the row itself — never from the EMI schedule. It was
        // fetching 24 rows per customer to display none of them.
        excludeAssociations: 'loanStatements'
      };
      let model = 'member_details';
      let filterObj = {};

      console.log(`Fetching data: ${filterType}, Page: ${page}, Limit: ${limit}, Branch: ${branchId}`);

      switch (filterType) {
        case "All":
          model = 'member_details';
          break;

        case "Pending":
          model = 'personal_details';
          filterObj = { data_operator_status: "pending" };
          params.fields = 'customerUniqueId,name,mobileNumber,id,fieldOfficerId,loan_requested,loan_request_message,data_operator_status,branchId';
          params.includeDepth = 0;
          break;

        // Customers already verified by the data entry operator.
        //
        // Verify flips data_operator_status pending -> verified and then
        // navigates to the loan form, so the row leaves the Pending queue the
        // moment it is verified — correct for a work queue, but it left the
        // customer reachable from NOWHERE if the operator cancelled out of the
        // form. Four customers on live were stranded in exactly that state with
        // their loan_requested amount intact and no way back to them.
        //
        // Same fields and includeDepth as Pending, and both matter:
        // personal_details has a hasMany without a sourceKey, so an unbounded
        // fetch scans millions of rows.
        case "Verified":
          model = 'personal_details';
          filterObj = { data_operator_status: "verified" };
          params.fields = 'customerUniqueId,name,mobileNumber,id,fieldOfficerId,loan_requested,loan_request_message,data_operator_status,branchId';
          params.includeDepth = 0;
          break;

        case "Submitted":
          model = 'member_details';
          filterObj = { dataEntryOperatorStatus: "submitted" };
          break;

        case "Processing":
          model = 'member_details';
          filterObj = { branchManagerStatus: "pending" };
          break;

        case "Disposed":
          model = 'member_details';
          filterObj = { adminStatus: "submitted" };
          break;

        case "RequestedLoan":
          model = 'personal_details';
          filterObj = { data_operator_status: "pending" };
          params.fields = 'customerUniqueId,name,mobileNumber,id,fieldOfficerId,loan_requested,loan_request_message,data_operator_status,branchId';
          break;

        default:
          break;
      }

      if (Object.keys(filterObj).length > 0) {
        params.filter = JSON.stringify(filterObj);
      }

      const response = await axios.get(`${url}/${model}`, { params });

      const { data: responseData, count } = response.data;

      setTableData(responseData || []);
      setPagination({
        page,
        limit,
        total: count || 0
      });

      // Update filter state if it changed
      if (currentFilter !== filterType && filterType !== "RequestedLoan") {
        setCurrentFilter(filterType);
      }

    } catch (error) {
      console.error(`Error fetching ${filterType} data:`, error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Error fetching ${filterType} data`,
        confirmButtonColor: "#004BB5",
        customClass: {
          popup: 'rounded-xl font-sans',
          confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
        }
      });
    } finally {
      setLoading(false);
    }
  }, [currentFilter, debouncedSearch, verificationFilter, data, selectedBranch, canViewAllBranches]);

  // Wrapper functions to maintain API compatibility with buttons
  const getMembersData = (page = 1, limit = pagination.limit) => {
    setCurrentFilter("All");
    setPagination(prev => ({ ...prev, page }));
  };

  const fetchPendingData = (page = 1, limit = pagination.limit) => {
    setCurrentFilter("Pending");
    setPagination(prev => ({ ...prev, page }));
  };

  const fetchVerifiedData = (page = 1, limit = pagination.limit) => {
    setCurrentFilter("Verified");
    setPagination(prev => ({ ...prev, page }));
  };

  const fetchSubmittedData = (page = 1, limit = pagination.limit) => {
    setCurrentFilter("Submitted");
    setPagination(prev => ({ ...prev, page }));
  };

  const fetchProcessingData = (page = 1, limit = pagination.limit) => {
    setCurrentFilter("Processing");
    setPagination(prev => ({ ...prev, page }));
  };

  const fetchDisposedData = (page = 1, limit = pagination.limit) => {
    setCurrentFilter("Disposed");
    setPagination(prev => ({ ...prev, page }));
  };

  // Pagination Change Handlers
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, page: 1, limit: newLimit }));
  };

  // Requested Loan Data for Modal (Separate fetch)
  const fetchRequestedLoanData = async () => {
    setLoading(true);
    try {
      const branchId = getBranchIdFilter();
      const params = {
        fields: 'customerUniqueId,name,mobileNumber,id,fieldOfficerId,loan_requested,loan_request_message,data_operator_status,branchId',
        filter: JSON.stringify({ data_operator_status: "pending" }),
        limit: 100, // Limit for modal
        branchId: branchId || undefined
      };

      const response = await axios.get(`${API_BASE_URL}/api/v2/getModelData/personal_details`, { params });
      setRequestedLoanData(response.data.data || []);
      setShowRequestedLoanModal(true);
    } catch (error) {
      console.log("Error fetching requested loan data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error fetching requested loan data",
        confirmButtonColor: "#004BB5",
        customClass: {
          popup: 'rounded-xl font-sans',
          confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const filterByBranch = (branchId) => {
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleBranchChange = (event, value) => {
    setSelectedBranch(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Unified useEffect to handle all data refetching
  useEffect(() => {
    fetchData(pagination.page, pagination.limit, currentFilter, debouncedSearch, verificationFilter);
  }, [pagination.page, pagination.limit, currentFilter, selectedBranch, debouncedSearch, verificationFilter, fetchData]);

  const handleVerify = async (id) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/addDetails`,
        {
          model: "personal_details",
          type: "Edit",
          id: id,
          data_operator_status: "verified",
        }
      );
      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: "success",
          title: "Verified",
          text: "Status updated to 'verified' successfully!",
          confirmButtonColor: "#004BB5",
          customClass: {
            popup: 'rounded-xl font-sans',
            confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
          }
        });
        // Refresh current view
        if (showRequestedLoanModal) {
          fetchRequestedLoanData();
        } else {
          fetchData(pagination.page, pagination.limit, currentFilter);
        }
        navigate("/addEditBusinessLoan", { state: { type: "Add", customerId: id } });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update status. Please try again.",
        confirmButtonColor: "#004BB5",
        customClass: {
          popup: 'rounded-xl font-sans',
          confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
        }
      });
    }
  };

  const fetchCustomerOptions = async () => {
    try {
      const branchFilter = canViewAllBranches ? null : (data?.branchId || selectedBranch?.id);
      const options = await getCustomerOptions(branchFilter);
      // options now include name, no need to map manually
      setCustomerOptions(options);
    } catch (error) {
      console.error("Error fetching customer options:", error);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchCustomerOptions();
  }, []);

  const handleAddClick = () => {
    if (!customerId) {
      setError("Please select a customer");
      return;
    }
    setError("");
    navigate("/addEditBusinessLoan", { state: { type: "Add", customerId } });
  };

  // Filters that fetch `personal_details` (the CUSTOMER) rather than
  // `member_details` (a loan application). The two shapes are not
  // interchangeable: personal_details rows have no adminStatus, no verification
  // fields, and no proposed loan — and deleting one removes the customer.
  // Anything that assumes a member_details row must be hidden for these.
  const isPersonalDetailsView =
    currentFilter === "Pending" ||
    currentFilter === "RequestedLoan" ||
    currentFilter === "Verified";

  // WHICH ID IS THIS? The answer depends on the tab, and getting it wrong opens
  // a different customer's record.
  //
  // personal_details.id and member_details.id are two independent AUTO_INCREMENT
  // sequences with no relationship. They drift apart with every customer who
  // takes no loan and every customer who takes a second one — 34 customers
  // against 31 applications here. So the SAME number means different things:
  //
  //     personal_details.id 26 = Jerry   (a customer, no application yet)
  //     member_details.id   26 = Tom's loan
  //
  // Passing Jerry's 26 as `memberId` made AddEditBusinessLoan query
  // `member_details WHERE id = 26` and load TOM. Not just a display fault — the
  // operator could have edited or submitted against the wrong customer.
  //
  // handleVerify already does this correctly (it passes `customerId`), and
  // AddEditBusinessLoan accepts either and branches on which it was given.
  const loanFormState = (type, id) =>
    isPersonalDetailsView
      ? { type, customerId: id }
      : { type, memberId: id };

  const handleEditClick = (id) => {
    navigate("/addEditBusinessLoan", { state: loanFormState("Edit", id) });
  };

  const handleViewClick = (id) => {
    navigate("/addEditBusinessLoan", { state: loanFormState("View", id) });
  };

  const handleDownloadClick = async () => {
    try {
      setLoading(true);
      const branchId = getBranchIdFilter();
      const params = {
        branchId: branchId || undefined,
        includeDepth: 1,
        limit: 100000 // Large limit for export
      };

      const response = await axios.get(
        `${API_BASE_URL}/api/getModelData/member_details`,
        { params }
      );

      const dataToExport = response.data.data;

      // Get branch names for display
      const branchMap = {};
      branches.forEach(branch => {
        branchMap[branch.id] = branch.branchName || 'Unknown Branch';
      });

      // Add branch name to each row for clarity
      const exportData = dataToExport.map(item => ({
        ...item,
        branchName: branchMap[item.branchId] || 'Unknown Branch'
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Business Loans');

      const date = new Date().toISOString().split('T')[0];
      const branchInfo = canViewAllBranches ? 'All_Branches' : `Branch_${branchMap[data?.branchId] || data?.branchId}`;
      const fileName = `Business_Loans_${branchInfo}_${date}.xlsx`;

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: "Exported",
        text: "Download completed successfully",
        confirmButtonColor: "#004BB5",
        customClass: {
          popup: 'rounded-xl font-sans',
          confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
        }
      });
    } catch (error) {
      console.log("Error downloading data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error downloading data",
        confirmButtonColor: "#004BB5",
        customClass: {
          popup: 'rounded-xl font-sans',
          confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelClick = useCallback((id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this business loan application!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      customClass: {
        popup: 'rounded-xl font-sans',
        confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold text-white',
        cancelButton: 'rounded-lg px-4 py-2 text-sm font-semibold text-white'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.post(`${API_BASE_URL}/api/delDetails`, {
            model: "member_details",
            id: id,
          }, {
            headers: {
              "x-token": jwt,
            },
          });
          if (res.data.message) {
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Data deleted successfully.",
              confirmButtonColor: "#004BB5",
              customClass: {
                popup: 'rounded-xl font-sans',
                confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
              }
            });
            fetchData(pagination.page, pagination.limit, currentFilter, debouncedSearch, verificationFilter);
          } else if (res.data.error) {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: res.data.error,
              confirmButtonColor: "#004BB5",
              customClass: {
                popup: 'rounded-xl font-sans',
                confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
              }
            });
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: err.response?.data?.error || "An error occurred.",
            confirmButtonColor: "#004BB5",
            customClass: {
              popup: 'rounded-xl font-sans',
              confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
            }
          });
        }
      }
    });
  }, [currentFilter, pagination, jwt, debouncedSearch, verificationFilter, fetchData]);

  const handleCloseModal = useCallback(() => {
    setShowRequestedLoanModal(false);
  }, []);

  // The DEO stage decides, not adminStatus.
  //
  // 5,052 of 6,636 loans carry deo=pending with adminStatus='Approved' — the April
  // import advanced only adminStatus — so reading adminStatus hid the pencil on
  // every application the DEO had never actually submitted.
  //
  // Must stay in step with assertApplicationEditable (writeGuards.js:196): a
  // pencil whose Save the server refuses is worse than no pencil.
  const isRowDisbursed = (row) =>
    !["pending", "retrigger"].includes(
      String(row?.dataEntryOperatorStatus || "").toLowerCase().trim()
    );

  const handleSuperAdminEdit = (item) => {
    setSelectedEditItem(item);
    setShowEditBusinessModal(true);
  };

  const renderCustomAction = (id) => {
    const item = tableData.find((item) => item.id === id);
    if (!item) return null;

    return (
      <div className="flex items-center gap-1.5">
        {(currentFilter === "Pending" || currentFilter === "RequestedLoan") && item.data_operator_status === "pending" && (
          <Button variant="primary" size="sm" onClick={() => handleVerify(id)}>
            Verify
          </Button>
        )}

        {/* Excludes Verified for the same reason as Pending/RequestedLoan: all
            three read `personal_details`, while this modal and isRowDisbursed
            expect a `member_details` row. isRowDisbursed tests `dataEntryOperatorStatus`,
            which personal_details does not have — so it reads undefined as
            "open/editable" and the button would render over the wrong shape. */}
        {roleName === "superadmin" && !isRowDisbursed(item) && !isPersonalDetailsView && (
          <button
            onClick={() => handleSuperAdminEdit(item)}
            title="Super Admin Edit"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/20 text-warning-600 bg-warning-50 border-warning-200 hover:bg-warning-100 hover:text-warning-700"
          >
            <i className="fas fa-edit"></i>
          </button>
        )}
      </div>
    );
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "No file selected",
        confirmButtonColor: "#004BB5"
      });
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const chunkSize = 50;
        const chunks = [];
        for (let i = 0; i < jsonData.length; i += chunkSize) {
          chunks.push(jsonData.slice(i, i + chunkSize));
        }

        try {
          // Note: using original API for import
          for (let i = 0; i < chunks.length; i++) {
            const response = await axios.post(
              `${API_BASE_URL}/api/importloandetails`,
              { chunk: chunks[i] }
            );

            if (response.status !== 200 && response.status !== 201) {
              throw new Error(`Failed to import chunk ${i + 1}`);
            }
          }

          Swal.fire({
            icon: "success",
            title: "Import Completed",
            text: "All data imported successfully!",
            confirmButtonColor: "#004BB5",
            customClass: {
              popup: 'rounded-xl font-sans',
              confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
            }
          });
          getMembersData();
        } catch (error) {
          console.error("Error sending data to backend:", error);
          Swal.fire({
            icon: "error",
            title: "Import Error",
            text: `Error importing data: ${error.message}`,
            confirmButtonColor: "#004BB5",
            customClass: {
              popup: 'rounded-xl font-sans',
              confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
            }
          });
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error reading file:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error reading file. Please try again.",
        confirmButtonColor: "#004BB5"
      });
    }
  };

  // Format table data with applicant name
  const formatTableData = () => {
    return tableData.map(item => {
      // Determine source of customer ID and Name based on structure
      let customerUniqueId = "N/A";
      let applicantName = "N/A";
      const personalDetails = item.member_details_belongsTo_personal_details_customerId;

      // Case 1: item is from member_details (All, Submitted, Processing, Disposed)
      if (personalDetails) {
        customerUniqueId = personalDetails.customerUniqueId || "N/A";
      }
      // Case 2: item is from personal_details (Pending)
      else if (item.customerUniqueId) {
        customerUniqueId = item.customerUniqueId;
      }

      // FIX 5B: Determine Name — priority: personal_details.name > applicantName > "N/A"
      // Verified belongs with these: all three fetch `personal_details`, where
      // the name is on the row itself. Without it the member_details branch
      // below looks for an association these rows do not have and every
      // Verified applicant renders as "N/A".
      // Hoisted out of the else-branch below: the amount block also needs it, to
      // fall back to the PROPOSED amount before approval stamps amountToRelease.
      // Undefined on personal_details rows, which have no such association.
      const loanDetails = item.fk_member_details_hasOne_proposed_loan_details_memberId;

      if (isPersonalDetailsView) {
        // For pending, item is personal_details, so name is directly there
        applicantName = item.name || "N/A";
      } else {
        // For others, item is member_details
        // Priority: personal_details.name (most reliable) > applicantName from proposed loan > N/A
        applicantName = personalDetails?.name || loanDetails?.applicantName || "N/A";
      }

      // Format the sanctioned amount.
      //
      // The column stays 0 until the loan is approved, at which point
      // addDetails.js stamps in proposed_loan_details.loanAmount. That 0 is not a
      // zero-rupee loan — it means "not sanctioned yet" — so it shows as a dash
      // rather than "0", which read as a real figure on an application awaiting
      // approval. A MISSING value still shows N/A; the two states are different.
      let formattedAmount = "N/A";
      // personal_details rows carry no amountToRelease — that column lives on
      // member_details and is stamped at approval. On those tabs the figure the
      // field officer asked for is `loan_requested`, already fetched above, so
      // without this every Requested Loan row read "N/A" while the amount sat
      // one column away.
      //
      // On member_details rows, amountToRelease is 0 until the loan is APPROVED,
      // so an application still awaiting submission showed a dash and the
      // operator could not see what had been applied for while deciding whether
      // to submit it. proposed_loan_details.loanAmount carries the figure from
      // the moment the application is created, is ALREADY loaded on this row
      // (the applicant-name fallback above reads the same association), and is
      // also in PAISE — so formatINR applies to either without conversion.
      const isProposedAmount =
        !isPersonalDetailsView &&
        !(Number(item.amountToRelease) > 0) &&
        Number(loanDetails?.loanAmount) > 0;

      const rawAmount = isPersonalDetailsView
        ? item.loan_requested
        : (isProposedAmount ? loanDetails.loanAmount : item.amountToRelease);
      if (
        rawAmount !== null &&
        rawAmount !== undefined &&
        rawAmount !== "" &&
        rawAmount !== "N/A"
      ) {
        // amountToRelease stores PAISE (Stage 2); formatINR converts for display.
        // Without it a Rs.1,00,000 loan would show as 1,00,00,000.
        const numAmount = parseFloat(rawAmount);
        if (isNaN(numAmount)) {
          formattedAmount = "N/A";
        } else if (numAmount <= 0) {
          formattedAmount = "—";
        } else if (isPersonalDetailsView) {
          // THE UNITS DIFFER — do not share formatINR here.
          //
          //   member_details.amountToRelease   10000000  (paise)  = Rs.1,00,000
          //   personal_details.loan_requested  "100000"  (rupees) = Rs.1,00,000
          //
          // Both are Tom's same loan. formatINR divides by 100, so passing
          // loan_requested through it would show a Rs.2,50,000 request as
          // Rs.2,500. It is also a STRING, hence the parseFloat above.
          formattedAmount = numAmount.toLocaleString("en-IN");
        } else if (isProposedAmount) {
          // Tagged, not shown bare. An approved sanction and a request are
          // different facts: rendering Rs.2,50,000 identically to a disbursed
          // loan would read as "Jerry HAS a Rs.2,50,000 loan", which is not yet
          // true. ServerTablePagination returns the cell value as-is, so a React
          // element here needs no change to the shared table.
          formattedAmount = (
            <span className="inline-flex items-baseline gap-1.5">
              <span>{formatINR(numAmount)}</span>
              <span className="text-2xs font-medium uppercase tracking-wide text-ink-400">
                proposed
              </span>
            </span>
          );
        } else {
          formattedAmount = formatINR(numAmount);
        }
      }

      const formattedItem = {
        ...item,
        customerUniqueId,
        applicantName,
        amountToRelease: formattedAmount,
        // Branch name might differ if it's included or not
        branchName: branches.find(b => b.id === Number(item.branchId))?.branchName || "N/A"
      };

      formattedItem._html_attributes = {
        applicantName: {
          'data-field': 'applicantName',
          className: 'align-left'
        },
        customerUniqueId: {
          'data-field': 'customerUniqueId',
          className: 'align-left'
        },
        amountToRelease: {
          'data-field': 'amountToRelease',
          className: 'align-right'
        }
      };

      return formattedItem;
    });
  };

  const customHeaders = {
    applicantName: {
      label: "APPLICANT NAME",
      'data-field': 'applicantName',
      className: 'align-left'
    },
    customerUniqueId: {
      label: "CUSTOMER ID",
      'data-field': 'customerUniqueId',
      className: 'align-left'
    },
    amountToRelease: {
      // The column holds the SANCTIONED amount, not the cash handed over — the
      // charges (processing, GST, security deposit, insurance) are still inside
      // it. "Amount to Release" read as cash out and overstated it: live loan
      // 6436 shows 1,00,000 here while 87,640 actually left the branch.
      // Day-End heads the same field neutrally as "Amount (₹)".
      label: "LOAN AMOUNT",
      'data-field': 'amountToRelease',
      className: 'align-right'
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const getDisplayData = () => {
    return formatTableData();
  };

  const filterOptions = [
    { label: "All", value: "All" },
    { label: "Requested Loan", value: "Pending" },
    // Follows "Requested Loan" because that is the step after it: a customer
    // verified here but whose application was never completed would otherwise
    // be unreachable from this screen.
    { label: "Verified", value: "Verified" },
    { label: "Submitted", value: "Submitted" },
    { label: "Processing", value: "Processing" },
    { label: "Disposed", value: "Disposed" },
  ];

  const headerActions = (
    <div className="flex flex-wrap items-center gap-3">
      {hasAddPermission && (
        <div className="flex items-center gap-2">
          <Autocomplete
            options={customerOptions}
            getOptionLabel={(option) => {
              const name = option.name || "Unknown";
              const label = option.label || "No ID";
              return `${name} (${label})`;
            }}
            value={customerOptions.find((option) => option.value === customerId) || null}
            onChange={(event, value) => {
              setCustomerId(value ? value.value : null);
              setError("");
            }}
            filterOptions={(options, { inputValue }) => {
              const lowerInput = inputValue.toLowerCase();
              return options.filter(option =>
                (option.name && option.name.toLowerCase().includes(lowerInput)) ||
                (option.label && option.label.toLowerCase().includes(lowerInput))
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Customer..."
                variant="outlined"
                size="small"
                error={!!error}
                helperText={error}
                sx={{
                  width: '240px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#F8FAFC',
                    height: '36px',
                    padding: '0 8px !important',
                    '& fieldset': {
                      borderColor: '#E2E8F0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#CBD5E1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#004BB5',
                    },
                  },
                  '& input': {
                    border: 'none !important',
                    backgroundColor: 'transparent !important',
                    boxShadow: 'none !important',
                    padding: '0 !important',
                    height: '100% !important',
                  },
                  '& .MuiFormHelperText-root': {
                    margin: 0,
                    position: 'absolute',
                    bottom: '-18px',
                    fontSize: '10px'
                  }
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />
          <Button
            onClick={handleAddClick}
            variant="primary"
            className="h-[36px] text-xs font-semibold px-4 whitespace-nowrap"
          >
            Create Application
          </Button>
        </div>
      )}
    </div>
  );

  const toolbarContent = (
    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 w-full">
      {/* Left: Search Input, All Customers Filter & Filter Tabs */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          placeholder="Search name or ID..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="h-[36px] w-64"
        />
        {/* Verified excluded with the other personal_details views: this filters
            on verification fields that only exist on member_details. */}
        {(isAdmin || isSuperAdmin) && !isPersonalDetailsView && (
          <Select
            value={verificationFilter}
            onChange={(e) => {
              setVerificationFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            options={[
              { label: "All Customers", value: "All" },
              { label: "Verified Customers", value: "Verified" },
              { label: "Not Verified Customers", value: "Not Verified" },
            ]}
          />
        )}
        {isDataEntryOperator && (
          <SegmentedControl
            value={currentFilter}
            onChange={(val) => {
              if (val === "Pending") {
                fetchPendingData(1);
              } else if (val === "Verified") {
                fetchVerifiedData(1);
              } else if (val === "Submitted") {
                fetchSubmittedData(1);
              } else if (val === "Processing") {
                fetchProcessingData(1);
              } else if (val === "Disposed") {
                fetchDisposedData(1);
              } else {
                getMembersData(1);
              }
            }}
            options={filterOptions}
          />
        )}
      </div>

      {/* Right: Select Customer & Create Application, Export/Import, Requested Loans */}
      <div className="flex flex-wrap items-center gap-3">
        {headerActions}
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              onClick={handleDownloadClick}
              variant="secondary"
              size="sm"
              leftIcon={<BsDownload className="w-3.5 h-3.5" />}
            >
              Export
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => document.getElementById("fileInput").click()}
            >
              Import
            </Button>
            <input
              id="fileInput"
              type="file"
              accept=".xlsx, .xls"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        )}

        {/* Verification count / modal trigger */}
        {(currentFilter === "Pending" || currentFilter === "RequestedLoan") && (
          <Button
            onClick={fetchRequestedLoanData}
            variant="secondary"
            size="sm"
          >
            Show Requested Loans
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <PageLayout
      toolbar={toolbarContent}
    >
      <div className="flex-grow flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="flex-grow flex items-center justify-center min-h-[200px]">
            <LogoLoader size="md" />
          </div>
        ) : (
          <ServerTablePagination
            key={currentFilter + selectedBranch?.id}
            tabledata={getDisplayData()}
            totalCount={pagination.total}
            page={pagination.page}
            rowsPerPage={pagination.limit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            showViewOption={hasViewPermission}
            showEditOption={(row) => hasEditPermission && !isRowDisbursed(row)}
            showDeleteOption={isSuperAdmin && !isPersonalDetailsView}
            onClickView={handleViewClick}
            onClickEdit={handleEditClick}
            onClickDel={handleDelClick}
            actionButtons={true}
            visibleFields={[
              "applicantName",
              "customerUniqueId",
              "amountToRelease"
            ]}
            customHeaders={customHeaders}
            updateList={() => fetchData(pagination.page, pagination.limit, currentFilter)}
            roleName={roleName}
            customAction={renderCustomAction}
            cellClassNames={{
              applicantName: "!text-left",
              customerUniqueId: "!text-left",
              amountToRelease: "!text-right font-data"
            }}
            headerClassNames={{
              applicantName: "!text-left",
              customerUniqueId: "!text-left",
              amountToRelease: "!text-right"
            }}
          />
        )}
      </div>

      {/* Requested Loan Modal */}
      <Modal
        isOpen={showRequestedLoanModal}
        onClose={handleCloseModal}
        title="Requested Loan Applications"
        size="xl"
      >
        <Table>
          <Table.Head>
            <tr>
              <Table.HeadCell align="center" className="w-[5%]">No</Table.HeadCell>
              <Table.HeadCell className="w-[20%] !text-left">Unique ID</Table.HeadCell>
              <Table.HeadCell className="w-[35%] !text-left">Name</Table.HeadCell>
              <Table.HeadCell align="center" className="w-[20%]">Mobile Number</Table.HeadCell>
              <Table.HeadCell align="center" className="w-[20%]">Action</Table.HeadCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {requestedLoanData.map((item, index) => (
              <Table.Row key={item.id}>
                <Table.Cell align="center" className="text-xs font-medium text-ink-500">{index + 1}</Table.Cell>
                <Table.Cell className="font-semibold text-primary-600 !text-left">{item.customerUniqueId}</Table.Cell>
                <Table.Cell className="font-medium text-ink-900 !text-left">{item.name}</Table.Cell>
                <Table.Cell align="center" className="font-data">{item.mobileNumber}</Table.Cell>
                <Table.Cell align="center">
                  {item.data_operator_status === "pending" && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleVerify(item.id)}
                    >
                      Verify
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Modal>

      {/* Edit Business Details Modal */}
      <EditBusinessDetailsModal
        show={showEditBusinessModal}
        onHide={() => setShowEditBusinessModal(false)}
        item={selectedEditItem}
        onSave={() => {
          setShowEditBusinessModal(false);
          fetchData(pagination.page, pagination.limit, currentFilter);
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Business details updated successfully",
            confirmButtonColor: "#004BB5",
            customClass: {
              popup: 'rounded-xl font-sans',
              confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
            }
          });
        }}
      />
    </PageLayout>
  );
};

export default BusinessLoan;

